/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  entertainmentPage,
  isSplash,
  getQuantity,
  getDayLabel,
  isNowAfterTime,
  updateBookedEntertainments,
  itWasBookedItem,
  getSelectedEntertainment,
  deleteEntertainmentsInCategory,
  handleDeleteEntertainment,
  handleDeleteClashedEvent,
  deleteEntertainmentByType,
  isExistsSameEntertainment,
} from '../../utils';
import { virtualPageView } from '../../googleTagManager';
import '../entertainment/Entertainment.scss';
import SwimmingOptions from '../swimmingOptions/SwimmingOptions';

const handleSaveSwimming = async (
  selectedTimeslots,
  selectedEntertainment,
  resId,
  siteCode,
  deleteEntertainment,
  getBookedEntertainments,
  saveEntertainment,
  getAvailableEntertainments,
  history,
  numOfUnits,
  shouldRemovePoolSlotOnOtherDay,
  bookedEvents,
  maxAllowedPoolSlots,
  bookedEntertainmentsWithoutPastEvents,
  maxSwimmingOnSameDay,
  isBeforeBreak,
  options,
) => {
  const canBookMoreSlotsOnSameDay = maxSwimmingOnSameDay > 1;

  await deleteEntertainmentsInCategory({
    selectedEntertainment,
    resId,
    siteCode,
    deleteEntertainment,
    bookedEntertainmentsWithoutPastEvents,
    skipToReportToGTM: canBookMoreSlotsOnSameDay,
    selectedTimeslots,
    options,
  });
  if (shouldRemovePoolSlotOnOtherDay && isBeforeBreak) deleteEntertainmentByType(deleteEntertainment, bookedEvents, resId, siteCode, 'splash');
  const { activityId, date, name, slots = [] } = selectedEntertainment;
  const entertainmentsToSave = selectedTimeslots.map(({ slotId: timeSlotId, guestNumber = 1 }) => {
    const [{ singleDate: selectedFullDate, duration }] = slots.filter(({ code }) => code === timeSlotId);
    return {
      resId,
      siteCode,
      activityId,
      date,
      name,
      timeSlotId,
      quantity: getQuantity(name, numOfUnits, guestNumber),
      selectedFullDate,
      duration,
      needToReportToGTM: !itWasBookedItem(bookedEntertainmentsWithoutPastEvents, timeSlotId),
      reportType: shouldRemovePoolSlotOnOtherDay
        || isExistsSameEntertainment(activityId, name, bookedEntertainmentsWithoutPastEvents) ? 'edit' : 'book',
    };
  });
  await saveEntertainment(entertainmentsToSave, 'swimming');
  await updateBookedEntertainments(resId, siteCode, getBookedEntertainments, getAvailableEntertainments);
  if (maxAllowedPoolSlots === 1) {
    history.push(entertainmentPage);
  } else {
    history.goBack();
  }
};

const getPageTitle = (date, title) => {
  if (!date) return '';
  if (date) return getDayLabel(date);
  return title;
};

const getIsAllowedToBookMorePoolSlots = (
  maxAllowedPoolSlots,
  arrivalDate,
  bookedEvents,
  poolRestrictionTime,
  isAllowedToBookMorePoolSlotsOnBreak,
) => {
  if (!isNowAfterTime(arrivalDate, poolRestrictionTime)) { // PRE-BREAK
    const bookedPoolSlots = bookedEvents.filter(({ name }) => isSplash(name));
    const numOfBookedPoolSlots = bookedPoolSlots.length;
    return numOfBookedPoolSlots < maxAllowedPoolSlots;
  }
  // ON BREAK
  return isAllowedToBookMorePoolSlotsOnBreak;
};

const getOptions = (selectedEntertainment = {}) => (selectedEntertainment.slots
  && selectedEntertainment.slots.length > 0 ? selectedEntertainment.slots : []);

const SwimmingDetail = ({
  title = '',
  subTitle = '',
  splashEntertainments,
  resId = 0,
  siteCode = '',
  getAvailableEntertainments = () => { },
  splashId = '',
  bookedEntertainmentsWithoutPastEvents = [],
  shouldFetchBookedEntertainments = false,
  saveEntertainment = () => { },
  deleteEntertainment = () => { },
  getBookedEntertainments = () => { },
  shouldFetchAvailableEntertainments,
  shouldSkip,
  separatorLabel = '',
  optionTitle = '',
  bookedEvents = [],
  bookedEventsWithPastEvents = [],
  numOfUnits,
  numOfGuests,
  maxAllowedPoolSlots = 1,
  arrivalDate,
  maxAllowedPoolSlotsOnSameDay,
  poolRestrictionTime,
  maxSwimmingOnSameDay,
  overlapCheckPoolItems,
  overlapAllowedMinutes,
  preventOverlapMandatory,
  setNumberOfGuestsForSwimming,
  numberOfGuestsForSwimming,
  refreshTimeInterval,
  lastConfigCheckDateTime,
  isBeforeBreak,
  isAllowedToBookMorePoolSlotsOnBreak,
  numOfPreBreakMaxAllowedPoolSlots,
  isAfterDailyOpeningTime,
  backstopDate,
  shouldFetchBackstopDate,
  getBackstopDateFromApi,
}) => {
  const history = useHistory();
  const [selectedEntertainment, setSelectedEntertainment] = useState({});
  const [checkedEntertainmentSlots, setCheckedEntertainmentSlots] = useState([]);

  useEffect(() => {
    virtualPageView('plan my day', 'book swimming', '/ga-virtual/plan-my-day/book-swimming/availability');
  }, []);

  useEffect(() => {
    if (shouldFetchAvailableEntertainments) getAvailableEntertainments(siteCode);
  }, [
    shouldFetchAvailableEntertainments,
    getAvailableEntertainments,
    siteCode,
  ]);

  useEffect(() => {
    if (splashEntertainments
      && splashEntertainments.length > 0
      && splashId !== ''
    ) {
      const cutsEntertainmentId = splashId.split('-');
      const dateIndex = cutsEntertainmentId[0];
      if (dateIndex) {
        const filteredEvent = { ...splashEntertainments[dateIndex].events.find(event => event.code === splashId) };
        const event = getSelectedEntertainment(filteredEvent);
        setSelectedEntertainment(event);
      }
    }
    if (splashId === '') {
      setSelectedEntertainment({});
      setCheckedEntertainmentSlots([]);
    }
  }, [
    setSelectedEntertainment,
    splashEntertainments,
    setCheckedEntertainmentSlots,
    splashId,
  ]);

  useEffect(() => {
    if (bookedEntertainmentsWithoutPastEvents
      && selectedEntertainment
      && selectedEntertainment.slots
      && selectedEntertainment.slots.length > 0
    ) {
      const bookedSlots = selectedEntertainment.slots
        .filter(({ code }) => {
          const bookedEntertainment = bookedEntertainmentsWithoutPastEvents.find(({ TimeSlotId: timeSlotId }) => code === timeSlotId);
          return !!bookedEntertainment;
        })
        .map(({ code }) => parseInt(code, 10));

      setCheckedEntertainmentSlots(bookedSlots);
    }
  }, [
    bookedEntertainmentsWithoutPastEvents,
    getBookedEntertainments,
    resId,
    siteCode,
    selectedEntertainment,
    setCheckedEntertainmentSlots,
    shouldFetchBookedEntertainments,
  ]);

  useEffect(() => {
    if (shouldFetchBookedEntertainments) getBookedEntertainments(resId, siteCode);
  }, [
    resId,
    siteCode,
    shouldFetchBookedEntertainments,
    getBookedEntertainments,
  ]);

  useEffect(() => {
    if (shouldSkip) history.push(entertainmentPage);
  }, [shouldSkip, history]);

  useEffect(() => {
    if (shouldFetchBackstopDate) getBackstopDateFromApi();
  }, [shouldFetchBackstopDate, getBackstopDateFromApi]);

  const options = getOptions(selectedEntertainment);
  const slotIds = options.map(({ code }) => code);

  return (
    <div>
      <div className="entertainment-main-container notSummaryPage">
        <div className="titles">
          <div className="title">{getPageTitle(selectedEntertainment && selectedEntertainment.date, title)}</div>
          <div className="sub-title">{(!!selectedEntertainment && selectedEntertainment.name) ? selectedEntertainment.name : subTitle}</div>
        </div>
        <div className="entertainments notSummaryPage">
          <SwimmingOptions
            title={optionTitle}
            separatorLabel={separatorLabel}
            options={options}
            saveAddon={(selectedTimeslots, shouldRemovePoolSlotOnOtherDay) => handleSaveSwimming(
              selectedTimeslots,
              selectedEntertainment,
              resId,
              siteCode,
              deleteEntertainment,
              getBookedEntertainments,
              saveEntertainment,
              getAvailableEntertainments,
              history,
              numOfUnits,
              shouldRemovePoolSlotOnOtherDay,
              bookedEvents,
              maxAllowedPoolSlots,
              bookedEntertainmentsWithoutPastEvents,
              maxSwimmingOnSameDay,
              isBeforeBreak,
              slotIds,
            )}
            removeAddon={timeSlotId => handleDeleteEntertainment(
              selectedEntertainment,
              resId,
              siteCode,
              deleteEntertainment,
              getBookedEntertainments,
              getAvailableEntertainments,
              bookedEntertainmentsWithoutPastEvents,
              timeSlotId,
            )}
            removeClashedEvent={clashedEvent => handleDeleteClashedEvent(
              deleteEntertainment,
              resId,
              siteCode,
              clashedEvent,
            )}
            bookedSlotsOfThisEventWithoutPast={checkedEntertainmentSlots}
            isAllowedToBookMorePoolSlots={getIsAllowedToBookMorePoolSlots(
              maxAllowedPoolSlots,
              arrivalDate,
              bookedEventsWithPastEvents,
              poolRestrictionTime,
              isAllowedToBookMorePoolSlotsOnBreak,
            )}
            bookedEvents={bookedEvents}
            bookedEventsWithPastEvents={bookedEventsWithPastEvents}
            quantityToBook={getQuantity(selectedEntertainment.name, numOfUnits, numOfGuests)}
            maxAllowedPoolSlots={maxAllowedPoolSlots}
            arrivalDate={arrivalDate}
            maxAllowedPoolSlotsOnSameDay={maxAllowedPoolSlotsOnSameDay}
            numOfGuests={numOfGuests}
            poolRestrictionTime={poolRestrictionTime}
            overlapCheckPoolItems={overlapCheckPoolItems}
            overlapAllowedMinutes={overlapAllowedMinutes}
            preventOverlapMandatory={preventOverlapMandatory}
            setNumberOfGuestsForSwimming={setNumberOfGuestsForSwimming}
            numberOfGuestsForSwimming={numberOfGuestsForSwimming}
            refreshTimeInterval={refreshTimeInterval}
            lastConfigCheckDateTime={lastConfigCheckDateTime}
            numOfPreBreakMaxAllowedPoolSlots={numOfPreBreakMaxAllowedPoolSlots}
            isAfterDailyOpeningTime={isAfterDailyOpeningTime}
            backstopDate={backstopDate}
          />
        </div>
      </div>
    </div>
  );
};

SwimmingDetail.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  splashEntertainments: PropTypes.array,
  resId: PropTypes.number,
  siteCode: PropTypes.string,
  splashId: PropTypes.string,
  getAvailableEntertainments: PropTypes.func,
  bookedEntertainmentsWithoutPastEvents: PropTypes.array,
  shouldFetchBookedEntertainments: PropTypes.bool,
  saveEntertainment: PropTypes.func,
  deleteEntertainment: PropTypes.func,
  getBookedEntertainments: PropTypes.func,
  shouldFetchAvailableEntertainments: PropTypes.bool,
  shouldSkip: PropTypes.bool,
  separatorLabel: PropTypes.string,
  optionTitle: PropTypes.string,
  bookedEvents: PropTypes.array,
  bookedEventsWithPastEvents: PropTypes.array,
  numOfUnits: PropTypes.number,
  numOfGuests: PropTypes.number,
  maxAllowedPoolSlots: PropTypes.number,
  arrivalDate: PropTypes.string,
  maxAllowedPoolSlotsOnSameDay: PropTypes.number,
  poolRestrictionTime: PropTypes.string,
  maxSwimmingOnSameDay: PropTypes.number,
  overlapCheckPoolItems: PropTypes.bool,
  overlapAllowedMinutes: PropTypes.number,
  preventOverlapMandatory: PropTypes.bool,
  setNumberOfGuestsForSwimming: PropTypes.func,
  numberOfGuestsForSwimming: PropTypes.number,
  refreshTimeInterval: PropTypes.number,
  lastConfigCheckDateTime: PropTypes.string,
  isBeforeBreak: PropTypes.bool,
  isAllowedToBookMorePoolSlotsOnBreak: PropTypes.bool,
  numOfPreBreakMaxAllowedPoolSlots: PropTypes.number,
  isAfterDailyOpeningTime: PropTypes.bool,
  backstopDate: PropTypes.string,
  shouldFetchBackstopDate: PropTypes.bool,
  getBackstopDateFromApi: PropTypes.func,
};

export default SwimmingDetail;
